import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "../services/api";
import type { ClientStatusType } from "../types";

export const useQueue = (artistId: string | undefined) => {
  const queryClient = useQueryClient();

  const queue = useQuery({
    queryKey: ["queue", artistId],
    queryFn: () => api.getQueue(artistId!),
    enabled: !!artistId,
    refetchInterval: 10000,
  });

  const stats = useQuery({
    queryKey: ["queue-stats", artistId],
    queryFn: () => api.getQueueStats(artistId!),
    enabled: !!artistId,
    refetchInterval: 10000,
  });

  const updateStatus = useMutation({
    mutationFn: ({
      clientId,
      status,
    }: {
      clientId: string;
      status: ClientStatusType;
    }) => api.updateClientStatus(clientId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["queue", artistId] });
      queryClient.invalidateQueries({ queryKey: ["queue-stats", artistId] });
    },
  });

  const removeClient = useMutation({
    mutationFn: (clientId: string) => api.removeClient(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["queue", artistId] });
      queryClient.invalidateQueries({ queryKey: ["queue-stats", artistId] });
    },
  });

  // 🆕 Fonction pour forcer le rechargement
  const refreshQueue = () => {
    queryClient.invalidateQueries({ queryKey: ["queue", artistId] });
    queryClient.invalidateQueries({ queryKey: ["queue-stats", artistId] });
  };

  return {
    queue: queue.data || [],
    stats: stats.data,
    isLoading: queue.isLoading || stats.isLoading,
    error: queue.error || stats.error,
    updateStatus,
    removeClient,
    refreshQueue, // 🆕 Expose cette fonction
  };
};

export const useClientPosition = (clientId: string | undefined) => {
  return useQuery({
    queryKey: ["client-position", clientId],
    queryFn: () => api.getClientPosition(clientId!),
    enabled: !!clientId,
    refetchInterval: 5000,
  });
};
